import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ref, onValue, set, get } from 'firebase/database';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CourseCatalog } from '../components/cursos/CourseCatalog';
import { CourseDetailModal } from '../components/cursos/CourseDetailModal';
import { CoursePlanModal } from '../components/cursos/CoursePlanModal';
import { VirtualClassroom } from '../components/cursos/VirtualClassroom';
import { COURSES_DATA } from '../data/coursesData';
import { database } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { useAdmin } from '../hooks/useAdmin';
import { normalizeCourse } from '../utils/courseStructure';
import '../styles/CursosVortex.css';

export default function CursosVortexPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isCursoPeriodoActivo, isAuthenticated } = useAuth();
  const { isAdmin } = useAdmin(user);

  // Acceso a cursos: única y exclusivamente haber adquirido el paquete del periodo activo o ser admin
  const isSubscribed = isCursoPeriodoActivo || isAdmin;

  const [currentView, setCurrentView]       = useState('catalog'); // 'catalog' | 'classroom'
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [coursesList, setCoursesList]       = useState(COURSES_DATA);

  // Modal de resumen de temario
  const [detailCourse, setDetailCourse]     = useState(null);
  const [isDetailOpen, setIsDetailOpen]     = useState(false);

  // Modal de adquisición de paquete (4 cursos incluidos)
  const [planCourse, setPlanCourse]         = useState(null);
  const [isPlanOpen, setIsPlanOpen]         = useState(false);

  // Carga cursos desde RTDB garantizando que los 4 cursos oficiales de EXANI-II siempre estén completos
  useEffect(() => {
    const coursesRef = ref(database, 'vortex_web_courses');
    const unsubscribe = onValue(coursesRef, (snap) => {
      if (snap.exists()) {
        const val = snap.val();
        const rtdbMap = {};
        Object.keys(val).forEach((k) => {
          rtdbMap[k] = normalizeCourse({ id: k, ...val[k] });
        });

        // Combinar COURSES_DATA con los de RTDB:
        // Si RTDB tiene el curso pero no tiene lecciones (o tiene menos lecciones que el oficial),
        // preservamos las lecciones completas de COURSES_DATA.
        const merged = COURSES_DATA.map((baseCourse) => {
          const rtdbCourse = rtdbMap[baseCourse.id];
          if (!rtdbCourse) return normalizeCourse(baseCourse);
          const baseLessonsCount = (baseCourse.lessons || []).length;
          const rtdbLessonsCount = (rtdbCourse.lessons || []).length || (rtdbCourse.temas || []).flatMap(t => t.lessons || []).length;
          if (baseLessonsCount > rtdbLessonsCount) {
            return normalizeCourse({ ...rtdbCourse, lessons: baseCourse.lessons, temas: normalizeCourse(baseCourse).temas });
          }
          return rtdbCourse;
        });

        // Añadir cursos extra si el admin creó alguno adicional
        Object.keys(rtdbMap).forEach((id) => {
          if (!merged.some((c) => c.id === id)) {
            merged.push(rtdbMap[id]);
          }
        });

        setCoursesList(merged);
      } else {
        // Si RTDB está vacío, usamos COURSES_DATA normalizado
        setCoursesList(COURSES_DATA.map(normalizeCourse));
      }
    }, (err) => {
      console.warn('[CursosVortexPage] Error al leer RTDB, usando cursos locales:', err);
      setCoursesList(COURSES_DATA.map(normalizeCourse));
    });

    return () => unsubscribe();
  }, []);

  /* ── Auto-open course from ?courseId=xxx (deep links) ── */
  useEffect(() => {
    const courseId = searchParams.get('courseId');
    if (!courseId) return;
    const course = coursesList.find((c) => c.id === courseId);
    if (course && isSubscribed) {
      setSelectedCourse(course);
      setCurrentView('classroom');
    }
  }, [searchParams, coursesList, isSubscribed]);

  /* ── Handlers ── */
  const handleOpenDetail = (course) => {
    setDetailCourse(course);
    setIsDetailOpen(true);
  };

  const handleOpenPlanModal = (course) => {
    setPlanCourse(course);
    setIsPlanOpen(true);
  };

  const handleSelectCourse = (course) => {
    // Si no tiene el paquete activo, se le muestra el modal de pago de $300 (4 cursos)
    if (!isSubscribed) {
      handleOpenPlanModal(course);
      return;
    }
    // Si tiene acceso, ingresa directo al aula virtual sin restricciones
    setSelectedCourse(course);
    setCurrentView('classroom');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToCatalog = () => {
    setCurrentView('catalog');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRequireAuth = () => {
    navigate('/purchase-license');
  };

  return (
    <div className="vx-cursos-page">
      {currentView !== 'classroom' && <Header />}

      <main className={`vx-cursos-main ${currentView === 'classroom' ? 'vx-cursos-main-full' : ''}`}>
        {currentView === 'catalog' ? (
          <CourseCatalog
            courses={coursesList}
            isSubscribed={isSubscribed}
            isAuthenticated={isAuthenticated}
            onSelectCourse={handleSelectCourse}
            onOpenDetailModal={handleOpenDetail}
            onEnroll={handleOpenPlanModal}
            onOpenAuth={handleRequireAuth}
          />
        ) : selectedCourse ? (
          <VirtualClassroom
            course={selectedCourse}
            uid={user?.uid || null}
            userName={user?.displayName || (user?.email ? user.email.split('@')[0] : 'Estudiante')}
            userEmail={user?.email || ''}
            onBackToCatalog={handleBackToCatalog}
            isSubscribed={isSubscribed}
          />
        ) : null}
      </main>

      {/* ── Modal Resumen de Temario Completo ── */}
      <CourseDetailModal
        course={detailCourse}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        isSubscribed={isSubscribed}
        isAuthenticated={isAuthenticated}
        onEnroll={handleOpenPlanModal}
        onStartCourse={handleSelectCourse}
      />

      {/* ── Modal Adquisición del Paquete de Cursos ($300 MXN / 4 cursos) ── */}
      <CoursePlanModal
        isOpen={isPlanOpen}
        onClose={() => setIsPlanOpen(false)}
        course={planCourse}
        user={user}
        isAuthenticated={isAuthenticated}
        onRequireAuth={handleRequireAuth}
      />

      {currentView !== 'classroom' && <Footer />}
    </div>
  );
}
